<template>
   <!-- ℹ️ This component is used to display a study card with its documents. -->
   <div
      class="mb-8 rounded-3xl border border-grey-20 p-6 text-base leading-snug text-black md:p-8"
      data-cy="user-study-card"
   >
      <div class="mb-10 flex flex-col gap-x-8 gap-y-4 md:flex-row">
         <!-- Logo will be added in V2 -->
         <!-- <img :src="study.logo" alt="" class="h-[7rem] w-[7rem] rounded-full bg-grey-20" /> -->
         <div class="flex w-full flex-col gap-x-8 gap-y-4 md:flex-row md:flex-wrap">
            <div
               class="flex grow flex-col gap-y-4 md:basis-[40ch]"
               :class="studyStatus.canWithdraw ? '' : 'max-w-60ch'"
            >
               <h3 class="text-bolder text-xl" data-cy="study-title" :lang="differentStudyLocale">{{ study.title }}</h3>
               <div class="flex flex-col flex-wrap items-start gap-4 md:flex-row md:items-center">
                  <PillTag
                     class="shrink-0"
                     :text="studyStatus.text"
                     :theme="studyStatus.theme"
                     :hasDot="studyStatus.hasDot"
                     data-cy="study-status"
                  />
                  <PillTag
                     v-if="studyStatus.actionRequired"
                     class="shrink-0"
                     :text="$t('account.studies.action_required_pill')"
                     :theme="Theme.Sand"
                     data-cy="study-pending-action-tag"
                  />
                  <p class="shrink-0 text-grey-70" data-cy="study-dates">
                     <span v-if="props.study.enrolledAt" class="date"
                        >{{ $t("account.studies.date_labels.joined") }}
                        <time :datetime="props.study.enrolledAt">{{
                           useFormattedDate(props.study.enrolledAt, "short")
                        }}</time>
                     </span>
                     <span v-if="props.study.completedAt" class="date">
                        {{ $t("account.studies.date_labels.completed") }}
                        <time :datetime="props.study.completedAt">{{
                           useFormattedDate(props.study.completedAt, "short")
                        }}</time>
                     </span>
                     <span v-else-if="props.study.status === 'withdrawn'" class="date">
                        {{ $t("account.studies.date_labels.withdrawn") }}
                        <time :datetime="props.study.withdrawnAt">{{
                           useFormattedDate(props.study.withdrawnAt, "short")
                        }}</time>
                     </span>
                     <span v-else-if="props.study.status === 'screened_out'" class="date">
                        {{ $t("account.studies.date_labels.screened_out") }}
                        <time :datetime="props.study.screenedOutAt">{{
                           useFormattedDate(props.study.screenedOutAt, "short")
                        }}</time>
                     </span>
                  </p>
               </div>
               <p data-cy="study-description" :lang="differentStudyLocale">{{ study.description }}</p>
            </div>
            <div class="shrink-0 md:self-center">
               <!-- Continue enrolment button (only for pending status) -->
               <LinkButton
                  v-if="studyStatus.continueEnrolment"
                  data-cy="study-continue-enrolment-btn"
                  class="mb-6"
                  :url="`flow/${props.study.joiningFlowId}`"
                  :localeId="props.study.localeId"
                  :color="Theme.BrandDark"
                  styleType="filled"
                  roundedEdges="3xl"
               >
                  {{ $t("account.studies.continue_enrolment_label") }}
                  <span class="sr-only"> - {{ study.title }}</span>
               </LinkButton>
               <!-- Withdrawal button - for active and pending statuses -->
               <DefaultButton
                  v-if="studyStatus.canWithdraw"
                  :color="Theme.Danger"
                  data-cy="study-withdraw-btn"
                  styleType="outlined"
                  roundedEdges="3xl"
                  @click="onWithdrawClick"
                  >{{
                     props.study.status === "pending"
                        ? $t("account.studies.withdraw_enrolment_label")
                        : $t("account.studies.withdraw_label")
                  }}
                  <span class="sr-only"> - {{ study.title }}</span>
               </DefaultButton>
            </div>
         </div>
      </div>
      <div data-cy="study-reports" v-if="studyReports.length">
         <h4 class="text-bolder text-lg mb-4" data-cy="study-reports-title">Study reports</h4>
         <DocumentsTable :docs="studyReports" />
      </div>
      <div data-cy="other-documents" v-if="otherDocuments.length">
         <h4 v-if="studyReports.length" class="text-bolder text-lg mb-4 mt-8" data-cy="other-documents-title">
            Other documents
         </h4>
         <DocumentsTable :docs="otherDocuments" />
      </div>

      <div v-else-if="clientSettings.userDocuments.isActive" data-cy="no-docs-yet">
         {{ $t("account.docs.no_docs") }}
      </div>
   </div>
</template>
<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { Theme } from "@/types";
import type { DocumentResp } from "@/data/api-layer/documentsApi";
import type { UserStudyResp } from "@/data/api-layer/studiesApi";
import type { PillTagTheme } from "@/components/ui/PillTag.vue";
import { useLocaleStore } from "@/stores/localeStore";
import { useFormattedDate } from "@/composables/useFormattedDate";
import { studyReportTypes } from "@/constants/study-reports.ts";

const $t = useI18n().t;
const localeStore = useLocaleStore();
const clientSettings = inject(useClientSettingsKey);

interface Props {
   study: UserStudyResp;
   docs: DocumentResp[];
}

interface StudyStatus {
   theme: PillTagTheme;
   text: string;
   canWithdraw: boolean;
   continueEnrolment?: boolean; // only for pending status
   actionRequired?: boolean; // only for pending status
   hasDot?: boolean; // only for active status
}

const props = defineProps<Props>();
const emit = defineEmits(["start-withdrawal"]);

const studyStatus = computed((): StudyStatus => {
   if (props.study.completedAt)
      return {
         theme: Theme.Grey,
         text: $t("account.studies.stages.study_ended"),
         canWithdraw: false,
      };
   else if (props.study.status === "active")
      return {
         theme: Theme.Green,
         text: $t("account.studies.stages.participating"),
         canWithdraw: true,
         hasDot: true,
      };
   else if (props.study.status === "pending")
      return {
         theme: Theme.Sand,
         text: $t("account.studies.stages.pending"),
         canWithdraw: true,
         continueEnrolment: props.study.joiningFlowId !== null,
         actionRequired: true,
      };
   else if (props.study.status === "withdrawn")
      return {
         theme: Theme.Grey,
         text: $t("account.studies.stages.withdrawn"),
         canWithdraw: false,
      };
   else if (props.study.status === "screened_out")
      return {
         theme: Theme.Grey,
         text: $t("account.studies.stages.screened_out"),
         canWithdraw: false,
      };
});

// Fetched study title and description might be in different language than the current page's locale
// If so, we need to add lang attribute to the elements
const differentStudyLocale = computed(() =>
   localeStore.currentLocale?.id === props.study.localeId ? null : props.study.localeId
);

const studyReports = computed(() => props.docs.filter((doc) => studyReportTypes.includes(doc.type?.name)));
const otherDocuments = computed(() => props.docs.filter((doc) => !studyReportTypes.includes(doc.type?.name)));

function onWithdrawClick() {
   emit("start-withdrawal", props.study.studyId, props.study.title);
}
</script>
<style lang="scss" scoped>
// Add slash only between dates (sometimes we have only one date, sometimes two dates, and sometimes no dates at all)
.date + .date {
   &::before {
      content: " / ";
   }
}
</style>
